/** Levels adjustment — pure math + types, no React, no Fabric.
 *
 *  Lives outside the React component so the renderer-bound parts
 *  (`AdjustmentsModal`, `LevelsPanel`) can import it without
 *  dragging any UI deps, and so the same math can later be reused
 *  by the non-destructive adjustment-layer slow path. */

export type LevelsChannel = 'rgb' | 'r' | 'g' | 'b';

export interface LevelsChannelConfig {
  /** Input black point — pixel values ≤ this map to outputBlack. 0–254. */
  inputBlack: number;
  /** Input white point — pixel values ≥ this map to outputWhite. 1–255. */
  inputWhite: number;
  /** Midtone gamma. >1 brightens midtones, <1 darkens. 0.10–9.99. */
  gamma: number;
  /** Output range floor — the minimum value pixels can be remapped to. 0–254. */
  outputBlack: number;
  /** Output range ceiling — the maximum value pixels can be remapped to. 1–255. */
  outputWhite: number;
}

export interface LevelsValue {
  rgb: LevelsChannelConfig;
  r: LevelsChannelConfig;
  g: LevelsChannelConfig;
  b: LevelsChannelConfig;
}

/** Identity config — input = full range, gamma = 1, output = full range.
 *  Re-applied to a pixel produces no change. */
export const identityLevelsChannel = (): LevelsChannelConfig => ({
  inputBlack: 0,
  inputWhite: 255,
  gamma: 1,
  outputBlack: 0,
  outputWhite: 255,
});

export const identityLevelsValue = (): LevelsValue => ({
  rgb: identityLevelsChannel(),
  r: identityLevelsChannel(),
  g: identityLevelsChannel(),
  b: identityLevelsChannel(),
});

/** Build a 256-entry LUT for one channel's levels config.
 *
 *  Pipeline per pixel value `v`:
 *    1. Normalise to input range:  t = (v − inputBlack) / (inputWhite − inputBlack), clamped to [0,1]
 *    2. Apply midtone gamma:       t = t ^ (1 / gamma)
 *    3. Remap to output range:     out = outputBlack + t · (outputWhite − outputBlack)
 *    4. Clamp to [0,255], round.
 *
 *  Photoshop convention. With identity config, every entry equals
 *  its index — i.e., LUT[i] = i. */
export function buildLevelsLUT(c: LevelsChannelConfig): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256);
  const span = c.inputWhite - c.inputBlack;
  // Guard against `inputWhite <= inputBlack` (UI is bounded but a
  // future programmatic config could be malformed). When the input
  // range collapses to a point everything below maps to outputBlack
  // and everything ≥ maps to outputWhite — i.e., a hard threshold.
  const safeSpan = span <= 0 ? 1 : span;
  const invGamma = 1 / Math.max(c.gamma, 0.0001);
  const outSpan = c.outputWhite - c.outputBlack;
  for (let v = 0; v < 256; v++) {
    let t = (v - c.inputBlack) / safeSpan;
    if (t <= 0) {
      lut[v] = c.outputBlack;
      continue;
    }
    if (t >= 1) {
      lut[v] = c.outputWhite;
      continue;
    }
    t = Math.pow(t, invGamma);
    lut[v] = Math.round(c.outputBlack + t * outSpan);
  }
  return lut;
}

/** Compose the channel configs: a pixel's R goes through the
 *  combined RGB LUT first, then the per-channel R LUT (Photoshop's
 *  model — same shape as Curves). Source is `ImageData` so callers
 *  can cache the original pixels once and re-apply during live
 *  preview without paying for a getImageData round-trip every drag
 *  tick. */
/** Pure pixel transform — returns a fresh ImageData. Tests use
 *  this variant; the canvas-bound `applyLevelsToCanvas` is a thin
 *  putImageData wrapper. */
export function applyLevelsPixels(source: ImageData, value: LevelsValue): ImageData {
  const w = source.width;
  const h = source.height;
  const lutRGB = buildLevelsLUT(value.rgb);
  const lutR = buildLevelsLUT(value.r);
  const lutG = buildLevelsLUT(value.g);
  const lutB = buildLevelsLUT(value.b);
  const sd = source.data;
  const out = new Uint8ClampedArray(sd.length);
  for (let i = 0; i < sd.length; i += 4) {
    out[i] = lutR[lutRGB[sd[i]]];
    out[i + 1] = lutG[lutRGB[sd[i + 1]]];
    out[i + 2] = lutB[lutRGB[sd[i + 2]]];
    out[i + 3] = sd[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyLevelsToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: LevelsValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyLevelsPixels(source, value), 0, 0);
}
